// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { Button, Tag } from "@bfc-software/pallet";

import styles from "./action-tabs.module.css";

export type ActionCategory =
  "all" | "creations" | "modifications" | "deactivations";

const CATEGORIES: readonly ActionCategory[] = [
  "all",
  "creations",
  "modifications",
  "deactivations",
];

/**
 * The segmented "All / Creations / Modifications / Deactivations" filter
 * from the future-state design: a selected tab in `--pallet-role-primary`
 * (the design's navy), each tab's count as its own chip rather than folded
 * into the label.
 *
 * The count chip is `Tag` with tone `"neutral"` (`neutral-container`), not
 * pallet's `CountBadge` (`surface-container`) -- `CountBadge` reads correctly
 * against a plain page background at its other call sites, but nested in
 * this row's own `surface-container` track it is the same colour as its
 * parent and disappears. `Tag`'s neutral tone is a visibly different role, so
 * nothing collides.
 *
 * Outbound forbids a raw `<button>` (react/forbid-elements), and pallet's
 * `Button` has no slot for a child -- so the count sits beside it as a
 * sibling inside the `role="tab"` wrapper.
 */
export function ActionTabs({
  counts,
  selected,
  onChange,
  t,
}: {
  counts: Record<ActionCategory, number>;
  selected: ActionCategory;
  onChange: (next: ActionCategory) => void;
  t: TFunction;
}) {
  return (
    <div
      className={styles.track}
      role="tablist"
      aria-label={t("auditLog.tabs.ariaLabel")}
      data-test-role="audit-action-tabs"
    >
      {CATEGORIES.map((category) => (
        <span
          key={category}
          role="tab"
          aria-selected={selected === category}
          className={styles.tab}
          data-test-role={`audit-tab-${category}`}
        >
          <Button
            kind="text-only"
            variant={selected === category ? "primary" : "tertiary"}
            scale="compact"
            text={t(`auditLog.tabs.${category}`)}
            onPress={() => onChange(category)}
          />
          <Tag label={String(counts[category])} display={{ tone: "neutral" }} />
        </span>
      ))}
    </div>
  );
}
